import { createContext, useContext, useLayoutEffect, useMemo } from 'react'
import type { JSX, ReactNode } from 'react'

import {
  DEFAULT_LOCALE,
  directionOf,
  translate,
  type Direction,
  type Locale,
  type MessageKey,
  type Params,
} from '@lensmira/i18n'

/**
 * The counter's side of localisation: the language in effect, the accessor screens use, and the
 * two small components that keep mixed-direction text honest. `LOC-001`, `LOC-002`.
 *
 * `LOC-001` `MUST`: "Full UI translation with RTL support (Arabic, Urdu). RTL is a layout
 * requirement, not a font swap."
 *
 * Kept here rather than in `packages/i18n` so that package stays free of React and can be used by
 * the API's document renderer (`LOC-003`) without a UI library behind it.
 *
 * **With no provider mounted the language is English.** That is deliberate: every screen test that
 * predates localisation renders a bare screen, and they keep asserting English without change.
 */

const LocaleContext = createContext<Locale>(DEFAULT_LOCALE)

export interface LocaleProviderProps {
  readonly locale: Locale
  readonly children: ReactNode
}

/**
 * Provides the language and puts it on the document.
 *
 * `lang` and `dir` go on `<html>`, not on a wrapper `<div>`: the browser's own UI for the page
 * (scrollbars, form controls, the text cursor, `:dir()` and `:lang()` selectors, hyphenation, the
 * font selection `app.css` keys on `html[lang]`) all read the root. A wrapper would mirror the
 * layout and leave the scrollbar on the wrong side. This is what makes RTL a layout change and
 * not a font swap.
 */
export function LocaleProvider({ locale, children }: LocaleProviderProps): JSX.Element {
  useLayoutEffect(() => {
    const root = document.documentElement
    root.setAttribute('lang', locale)
    root.setAttribute('dir', directionOf(locale))
  }, [locale])

  return <LocaleContext.Provider value={locale}>{children}</LocaleContext.Provider>
}

export interface Translation {
  readonly locale: Locale
  readonly direction: Direction
  readonly t: (key: MessageKey, params?: Params) => string
}

/** The accessor screens use. A message is a whole sentence: never build one from two `t()` calls. */
export function useTranslation(): Translation {
  const locale = useContext(LocaleContext)
  return useMemo(
    () => ({
      locale,
      direction: directionOf(locale),
      t: (key: MessageKey, params?: Params) => translate(locale, key, params),
    }),
    [locale],
  )
}

/**
 * A run that must read left to right whatever the page direction: a job number, an invoice number,
 * a money amount, a dioptre value, a phone number.
 *
 * `DESIGN-TOKENS.md` §6: "Numbers, prices, dioptric values: No (mirroring)". A job number mirrored
 * is a different job number, and a dioptre sign on the wrong side of its digits is a different
 * prescription. `dir="ltr"` on the element, not a Unicode mark in the string, so copy and paste
 * and the screen reader both get the plain value.
 */
export function Ltr({
  children,
  className,
}: {
  readonly children: ReactNode
  readonly className?: string
}): JSX.Element {
  return (
    <span dir="ltr" {...(className === undefined ? {} : { className })}>
      {children}
    </span>
  )
}
